import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tableSavedViewsApi } from "../api/tableSavedViewsApi";
import {
  buildViewConfig,
  buildViewConfigFromTableLayout,
  getStickyColumnIndices,
  isSystemDefaultPreference,
  pickInitialView,
  readLastUsedViewId,
  resolveTableHeaders,
  viewConfigsMatch,
  writeLastUsedViewId,
} from "../utils/tableViewConfig";

const headersEqual = (a, b) =>
  a === b ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((value, index) => value === b[index]));

/**
 * Reusable saved-views state for tabular pages.
 */
export function useTableSavedViews({
  pageKey,
  allHeaders,
  fixedPrefixCount = 2,
  defaultStickyColumnKeys,
  getCurrentConfig,
  getExtraConfig,
  onApplyConfig,
  enabled = true,
}) {
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [displayHeaders, setDisplayHeaders] = useState(allHeaders || []);
  const [columnWidths, setColumnWidths] = useState({});
  const [stickyColumns, setStickyColumns] = useState([0, 1, 2]);
  const [loading, setLoading] = useState(() => Boolean(enabled && pageKey));
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(() => !enabled || !pageKey);
  const [isDirty, setIsDirty] = useState(false);
  const onApplyConfigRef = useRef(onApplyConfig);
  const getExtraConfigRef = useRef(getExtraConfig);
  const getCurrentConfigRef = useRef(getCurrentConfig);
  onApplyConfigRef.current = onApplyConfig;
  getExtraConfigRef.current = getExtraConfig;
  getCurrentConfigRef.current = getCurrentConfig;

  // Latest column layout (order/visibility/pinning) coming live from the
  // in-table column manager. The panel keeps this state internally and only
  // notifies us via callbacks, so we mirror it here. This lets an
  // argument-less `updateActiveView()` (e.g. the Views ▸ "Update" menu item)
  // persist the current pinning instead of falling back to defaults.
  const liveLayoutRef = useRef(null);
  // Last config applied from the server (select/save/load). Unsaved in-memory edits
  // may update `activeView.config` without touching this baseline.
  const savedConfigRef = useRef(null);

  const stickyKeysSignature = useMemo(
    () =>
      Array.isArray(defaultStickyColumnKeys)
        ? defaultStickyColumnKeys.join("||")
        : "",
    [defaultStickyColumnKeys]
  );

  const stickyKeys = useMemo(() => {
    if (stickyKeysSignature) {
      return stickyKeysSignature.split("||");
    }
    return (allHeaders || []).slice(0, 3);
  }, [allHeaders, stickyKeysSignature]);

  const applyConfig = useCallback(
    (config, viewMeta = null, options = {}) => {
      const headers = resolveTableHeaders(allHeaders, config, fixedPrefixCount);
      setDisplayHeaders((prev) => (headersEqual(prev, headers) ? prev : headers));
      setColumnWidths((prev) => {
        const next = config?.columnWidths || {};
        const prevKeys = Object.keys(prev || {});
        const nextKeys = Object.keys(next);
        if (
          prevKeys.length === nextKeys.length &&
          prevKeys.every((key) => prev[key] === next[key])
        ) {
          return prev;
        }
        return next;
      });
      const stickyIndices = getStickyColumnIndices(
        headers,
        config?.stickyColumns,
        stickyKeys.map((k) => (allHeaders || []).indexOf(k)).filter((i) => i >= 0)
      );
      setStickyColumns(stickyIndices);

      // Seed the live layout with the full applied state so that a later
      // partial edit (e.g. pinning a single column) merges onto this baseline
      // instead of wiping unrelated order/visibility/pinning on Update.
      const fixedCols = (allHeaders || []).slice(0, fixedPrefixCount);
      const allData = (allHeaders || []).slice(fixedPrefixCount);
      const visibleData = headers.slice(fixedPrefixCount);
      const hiddenData = allData.filter((h) => !visibleData.includes(h));
      const visibility = {};
      hiddenData.forEach((h) => {
        visibility[h] = false;
      });
      liveLayoutRef.current = {
        columnOrder: [...fixedCols, ...visibleData, ...hiddenData],
        columnVisibility: visibility,
        columnPinning: {
          left: stickyIndices.map((i) => headers[i]).filter(Boolean),
          right: [],
        },
      };

      savedConfigRef.current = config || {};

      if (typeof onApplyConfigRef.current === "function") {
        onApplyConfigRef.current(config || {}, viewMeta, options);
      }
      setIsDirty(false);
    },
    [allHeaders, fixedPrefixCount, stickyKeys]
  );

  const loadViews = useCallback(async () => {
    if (!enabled || !pageKey) {
      setIsReady(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await tableSavedViewsApi.list(pageKey);
      const list = Array.isArray(data) ? data : [];
      setViews(list);

      let initial = null;
      const lastUsedId = readLastUsedViewId(pageKey);
      const preferSystemDefault = isSystemDefaultPreference(lastUsedId);

      if (!preferSystemDefault && lastUsedId != null) {
        initial =
          list.find((v) => String(v.id) === String(lastUsedId)) || null;
      }
      if (!preferSystemDefault && !initial) {
        initial = pickInitialView(list);
      }

      if (initial?.config) {
        setActiveView(initial);
        applyConfig(initial.config, initial, { isInitialLoad: true });
        if (initial.id) writeLastUsedViewId(pageKey, initial.id);
      } else {
        setActiveView(null);
        applyConfig({}, null, { isInitialLoad: true });
        if (preferSystemDefault) {
          writeLastUsedViewId(pageKey, null);
        }
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load saved views";
      setError(message);
      setActiveView(null);
      applyConfig({}, null, { isInitialLoad: true });
    } finally {
      setLoading(false);
      setIsReady(true);
    }
  }, [applyConfig, enabled, pageKey]);

  const loadViewsStableRef = useRef(loadViews);
  loadViewsStableRef.current = loadViews;

  useEffect(() => {
    if (!pageKey || !enabled) {
      setIsReady(true);
      return;
    }
    loadViewsStableRef.current();
  }, [pageKey, enabled]);

  useEffect(() => {
    if (!allHeaders?.length || activeView) return;
    setDisplayHeaders((prev) => (headersEqual(prev, allHeaders) ? prev : allHeaders));
  }, [allHeaders, activeView]);

  const selectView = useCallback(
    async (view) => {
      if (!view) {
        setActiveView(null);
        writeLastUsedViewId(pageKey, null);
        applyConfig({}, null, { resetExtraConfig: true });
        // Clear isDefault so cold start/login does not revive a user view.
        try {
          const defaults = views.filter((v) => v.isDefault);
          if (defaults.length) {
            await Promise.all(
              defaults.map((v) =>
                tableSavedViewsApi.update(v.id, { isDefault: false })
              )
            );
            setViews((prev) => prev.map((v) => ({ ...v, isDefault: false })));
          }
        } catch {
          /* non-blocking */
        }
        return;
      }

      setActiveView(view);
      applyConfig(view.config || {}, view);
      writeLastUsedViewId(pageKey, view.id);

      try {
        const { data } = await tableSavedViewsApi.recordUsed(view.id);
        setViews((prev) =>
          prev.map((v) => (v.id === data.id ? data : v))
        );
        setActiveView(data);
      } catch {
        /* non-blocking */
      }

      // Persist selection as the login/cold-start default (localStorage alone
      // does not survive login in this app).
      try {
        const { data } = await tableSavedViewsApi.setDefault(view.id);
        setViews((prev) =>
          prev.map((v) => ({
            ...v,
            isDefault: v.id === data.id,
          }))
        );
        setActiveView((prev) =>
          prev && prev.id === data.id ? { ...prev, ...data, isDefault: true } : prev
        );
      } catch {
        /* non-blocking */
      }
    },
    [applyConfig, pageKey, views]
  );

  const captureConfig = useCallback((tableLayout) => {
    // Fall back to the live layout mirrored from the column manager so an
    // argument-less update still captures the current order/visibility/pinning.
    const layout = tableLayout || liveLayoutRef.current;
    const base =
      typeof getCurrentConfigRef.current === "function"
        ? getCurrentConfigRef.current(layout)
        : layout
          ? buildViewConfigFromTableLayout({
              allHeaders,
              columnOrder: layout.columnOrder,
              columnVisibility: layout.columnVisibility,
              columnPinning: layout.columnPinning,
              columnWidths,
              defaultStickyColumnKeys: stickyKeys,
              fixedPrefixCount,
            })
          : buildViewConfig({
              headers: displayHeaders,
              columnWidths,
              stickyColumns: stickyKeys,
            });
    const extra =
      typeof getExtraConfigRef.current === "function"
        ? getExtraConfigRef.current()
        : {};
    return { ...base, ...extra };
  }, [allHeaders, columnWidths, displayHeaders, fixedPrefixCount, stickyKeys]);

  const saveNewView = useCallback(
    async (name, { setAsDefault = false, tableLayout } = {}) => {
      const config = captureConfig(tableLayout);
      const { data } = await tableSavedViewsApi.create({
        pageKey,
        name: name.trim(),
        isDefault: setAsDefault,
        config,
      });
      setViews((prev) => [...prev, data]);
      await selectView(data);
      if (setAsDefault) {
        setViews((prev) =>
          prev.map((v) => ({
            ...v,
            isDefault: v.id === data.id,
          }))
        );
      }
      return data;
    },
    [captureConfig, pageKey, selectView]
  );

  const updateActiveView = useCallback(async (tableLayout) => {
    if (!activeView?.id) return null;
    const config = captureConfig(tableLayout);
    const { data } = await tableSavedViewsApi.update(activeView.id, { config });
    setViews((prev) => prev.map((v) => (v.id === data.id ? data : v)));
    setActiveView(data);
    applyConfig(data.config || {}, data);
    setIsDirty(false);
    return data;
  }, [activeView, applyConfig, captureConfig]);

  const renameView = useCallback(async (viewId, newName) => {
    const { data } = await tableSavedViewsApi.update(viewId, {
      name: newName.trim(),
    });
    setViews((prev) => prev.map((v) => (v.id === data.id ? data : v)));
    if (activeView?.id === data.id) setActiveView(data);
    return data;
  }, [activeView]);

  const deleteView = useCallback(
    async (viewId) => {
      await tableSavedViewsApi.remove(viewId);
      const remaining = views.filter((v) => v.id !== viewId);
      setViews(remaining);

      if (activeView?.id === viewId) {
        const next = pickInitialView(remaining);
        if (next) {
          await selectView(next);
        } else {
          await selectView(null);
        }
      }
    },
    [activeView, applyConfig, pageKey, selectView, views]
  );

  const setDefaultView = useCallback(async (viewId) => {
    const { data } = await tableSavedViewsApi.setDefault(viewId);
    setViews((prev) =>
      prev.map((v) => ({
        ...v,
        isDefault: v.id === data.id,
      }))
    );
    if (activeView?.id === data.id) setActiveView(data);
    return data;
  }, [activeView]);

  const updateColumnVisibility = useCallback(
    (visibleDataColumns) => {
      const fixed = (allHeaders || []).slice(0, fixedPrefixCount);
      const nextHeaders = [...fixed, ...visibleDataColumns];
      const config = buildViewConfig({
        headers: nextHeaders,
        columnWidths,
        stickyColumns: stickyKeys,
      });
      setDisplayHeaders(nextHeaders);
      setIsDirty(true);
      if (activeView) {
        setActiveView({ ...activeView, config });
      } else {
        applyConfig(config, null);
      }
    },
    [activeView, allHeaders, applyConfig, columnWidths, fixedPrefixCount, stickyKeys]
  );

  const handleColumnResize = useCallback((widths) => {
    setColumnWidths((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(widths || {});
      const unchanged =
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => prev[k] === widths[k]);
      if (unchanged) return prev;
      setIsDirty(true);
      return widths;
    });
  }, []);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const reconcileDirtyState = useCallback(() => {
    if (!activeView || !savedConfigRef.current) {
      setIsDirty(false);
      return;
    }
    const current = captureConfig();
    setIsDirty(!viewConfigsMatch(savedConfigRef.current, current));
  }, [activeView, captureConfig]);

  // Mirror live column-manager edits into `liveLayoutRef` (merging onto the
  // baseline seeded by applyConfig) so updates capture the latest layout.
  const handleLiveColumnOrderChange = useCallback((columnOrder) => {
    liveLayoutRef.current = { ...(liveLayoutRef.current || {}), columnOrder };
    setIsDirty(true);
  }, []);

  const handleLiveColumnVisibilityChange = useCallback((columnVisibility) => {
    liveLayoutRef.current = { ...(liveLayoutRef.current || {}), columnVisibility };
    setIsDirty(true);
  }, []);

  const handleLiveColumnPinningChange = useCallback((columnPinning) => {
    liveLayoutRef.current = { ...(liveLayoutRef.current || {}), columnPinning };
    setIsDirty(true);
  }, []);

  const controlLabel = useMemo(
    () => activeView?.name || "Default view",
    [activeView]
  );

  const columnLayoutKey = useMemo(
    () => (activeView?.id != null ? String(activeView.id) : "default"),
    [activeView?.id]
  );

  // ── Manage Columns: drive the rich in-table column panel from the Views menu ──
  // The panel needs *every* column (not just visible ones) so columns hidden by a
  // saved view still surface in its "Hidden" section and can be re-added. We hand
  // the full `allHeaders` to <Table> and express removed columns via
  // `columnVisibility`, keeping the saved view's order for visible columns.
  const allDataHeaders = useMemo(
    () => (allHeaders || []).slice(fixedPrefixCount),
    [allHeaders, fixedPrefixCount]
  );

  const visibleDataHeaders = useMemo(
    () => displayHeaders.slice(fixedPrefixCount),
    [displayHeaders, fixedPrefixCount]
  );

  const hiddenDataHeaders = useMemo(
    () => allDataHeaders.filter((h) => !visibleDataHeaders.includes(h)),
    [allDataHeaders, visibleDataHeaders]
  );

  const columnManagerVisibility = useMemo(() => {
    const visibility = {};
    hiddenDataHeaders.forEach((h) => {
      visibility[h] = false;
    });
    return visibility;
  }, [hiddenDataHeaders]);

  const columnManagerOrder = useMemo(
    () => [
      ...(allHeaders || []).slice(0, fixedPrefixCount),
      ...visibleDataHeaders,
      ...hiddenDataHeaders,
    ],
    [allHeaders, fixedPrefixCount, visibleDataHeaders, hiddenDataHeaders]
  );

  const columnManagerPinning = useMemo(
    () => ({
      left: (stickyColumns || []).map((i) => displayHeaders[i]).filter(Boolean),
      right: [],
    }),
    [stickyColumns, displayHeaders]
  );

  const [columnManagerOpen, setColumnManagerOpen] = useState(false);

  // Opens the rich, table-aware column manager as a separate modal popup.
  const openColumnManager = useCallback(() => {
    setColumnManagerOpen(true);
  }, []);

  const handleColumnManagerSaveView = useCallback(
    async (tableLayout) => {
      if (!activeView) return;
      await updateActiveView(tableLayout);
      setColumnManagerOpen(false);
    },
    [activeView, updateActiveView]
  );

  const handleColumnManagerSaveNewView = useCallback(
    async (viewName, tableLayout) => {
      if (!viewName?.trim()) return;
      await saveNewView(viewName, { tableLayout });
      setColumnManagerOpen(false);
    },
    [saveNewView]
  );

  const handleColumnManagerDeleteView = useCallback(async () => {
    if (!activeView?.id) return;
    if (
      !window.confirm(
        `Delete saved view "${activeView.name}"? This cannot be undone.`
      )
    ) {
      return;
    }
    await deleteView(activeView.id);
    setColumnManagerOpen(false);
  }, [activeView, deleteView]);

  // Spread directly onto <Table> to enable the Views-driven column manager.
  const columnManagerProps = useMemo(
    () => ({
      headers: allHeaders,
      columnLayoutKey,
      initialColumnOrder: columnManagerOrder,
      initialColumnVisibility: columnManagerVisibility,
      initialColumnPinning: columnManagerPinning,
      onColumnOrderChange: handleLiveColumnOrderChange,
      onColumnVisibilityChange: handleLiveColumnVisibilityChange,
      onColumnPinningChange: handleLiveColumnPinningChange,
      columnManagerOpen,
      onColumnManagerOpenChange: setColumnManagerOpen,
      onColumnManagerSaveView: activeView ? handleColumnManagerSaveView : undefined,
      columnManagerSaveViewDisabled: !isDirty,
      columnManagerSaveViewLabel: activeView
        ? `Update "${activeView.name}"`
        : "Save View",
      onColumnManagerSaveNewView: handleColumnManagerSaveNewView,
      columnManagerSaveNewViewLabel: "Save as new view",
      onColumnManagerDeleteView: activeView ? handleColumnManagerDeleteView : undefined,
      columnManagerDeleteViewLabel: activeView ? `Delete "${activeView.name}"` : undefined,
      columnManagerDeleteViewDisabled: loading,
    }),
    [
      allHeaders,
      columnLayoutKey,
      columnManagerOrder,
      columnManagerVisibility,
      columnManagerPinning,
      handleLiveColumnOrderChange,
      handleLiveColumnVisibilityChange,
      handleLiveColumnPinningChange,
      columnManagerOpen,
      handleColumnManagerSaveView,
      handleColumnManagerSaveNewView,
      handleColumnManagerDeleteView,
      activeView,
      isDirty,
      loading,
    ]
  );

  return {
    views,
    activeView,
    displayHeaders,
    columnWidths,
    setColumnWidths: handleColumnResize,
    stickyColumns,
    loading,
    error,
    isReady,
    isDirty,
    controlLabel,
    columnLayoutKey,
    loadViews,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    captureConfig,
    dataHeaders: displayHeaders.slice(fixedPrefixCount),
    // Column manager (Views ▸ "Manage columns" → rich in-table panel)
    columnManagerProps,
    onManageColumns: openColumnManager,
  };
}
